import type { Editor } from '@tiptap/core'
import { Sentry } from './sentry'
import { generatePlaceholder } from './imageCompression'
import { getImageUploadService } from './services/imageUploadServiceInstance'
import { primeLocalAttachment, releaseLocalAttachment } from './localAttachmentCache'
import { track, AnalyticsEvent } from './analytics'

/** Generate a nanoid-shaped id that matches the worker's CLIENT_ID_PATTERN. */
function generateAttachmentId(length = 16): string {
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-'
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  let out = ''
  for (let i = 0; i < length; i += 1) {
    out += alphabet[bytes[i] % alphabet.length]
  }
  return out
}

/**
 * Prepare an image for insertion: pick an attachment id, derive the canonical
 * HTTPS URL, prime the local-display cache, and return both so the caller can
 * insert the final URL straight into the editor. No base64 ever lands in
 * markdown.
 */
export function prepareImageForInsertion(
  file: File,
): { attachmentId: string; finalURL: string } {
  const prepared = prepareForInsertion(file)
  track(AnalyticsEvent.AttachmentAdded, { kind: 'image' })
  return prepared
}

/** Untracked core shared by the image + file entry points. */
function prepareForInsertion(
  file: File,
): { attachmentId: string; finalURL: string } {
  const attachmentId = generateAttachmentId()
  const service = getImageUploadService()
  const finalURL = service.attachmentURL(attachmentId)
  primeLocalAttachment(finalURL, file)
  return { attachmentId, finalURL }
}

/**
 * Prepare a non-image file for insertion. Identical contract to
 * `prepareImageForInsertion` — pick an id, derive the canonical URL, prime the
 * local-display cache — so the file chip can resolve its bytes (e.g. a
 * download) before the upload lands.
 */
export function prepareAttachmentForInsertion(
  file: File,
): { attachmentId: string; finalURL: string } {
  const prepared = prepareForInsertion(file)
  track(AnalyticsEvent.AttachmentAdded, { kind: 'file' })
  return prepared
}

/**
 * Upload a non-image attachment under a pre-chosen id. Mirrors
 * `uploadImageAndReplace` but skips the image-only LQIP placeholder step.
 */
export async function uploadAttachmentAndQueue(
  file: File,
  noteId: string,
  finalURL: string,
  attachmentId: string,
): Promise<void> {
  const service = getImageUploadService()
  try {
    const result = await service.upload(file, noteId, attachmentId)
    if ('url' in result) {
      // Upload landed — release the local blob, let the network serve bytes.
      releaseLocalAttachment(finalURL)
    }
    // If queued, keep the local blob alive until the queue retries drain.
  } catch (err) {
    // Expected failures (offline, network) are already handled by the upload
    // service, which enqueues for retry. Reaching this catch means the service
    // itself threw unexpectedly — report it.
    console.warn('[attachment-upload] Upload rejected:', err)
    Sentry.captureException(err)
  }
}

/**
 * Upload an image file to the server under a pre-chosen attachment id.
 *
 * Flow:
 *   1. Caller generated `attachmentId` + inserted the matching URL into the
 *      editor. Local bytes are already in `localAttachmentCache` so the
 *      editor shows the image immediately.
 *   2. Generate an LQIP placeholder and stash it on the image node so text
 *      editing doesn't disturb the visible preview.
 *   3. Delegate the actual upload + offline queueing to ImageUploadService.
 *   4. On success: release the local blob — the HTTPS URL now resolves
 *      normally from R2.
 */
export async function uploadImageAndReplace(
  editor: Editor,
  file: File,
  noteId: string,
  finalURL: string,
  attachmentId: string,
): Promise<void> {
  const service = getImageUploadService()

  try {
    const placeholder = await generatePlaceholder(file)
    setPlaceholderInEditor(editor, finalURL, placeholder)
  } catch (err) {
    console.warn('[image-upload] Failed to generate placeholder:', err)
  }

  try {
    const result = await service.upload(file, noteId, attachmentId)

    if ('url' in result) {
      // Upload landed — release the local blob, let the network serve bytes.
      releaseLocalAttachment(finalURL)
    }
    // If queued, keep the local blob alive until the queue retries drain.
  } catch (err) {
    // The service enqueues expected offline/network failures itself, so a throw
    // here is unexpected — report it.
    console.warn('[image-upload] Upload rejected:', err)
    Sentry.captureException(err)
  }
}

/**
 * Set the LQIP placeholder attribute on an image node identified by its src.
 */
function setPlaceholderInEditor(
  editor: Editor,
  src: string,
  placeholder: string,
): void {
  const { doc, tr } = editor.state
  let found = false

  doc.descendants((node, pos) => {
    if (found) return false
    if (node.type.name === 'image' && node.attrs.src === src) {
      tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        placeholder,
      })
      found = true
      return false
    }
  })

  if (found) {
    editor.view.dispatch(tr)
  }
}
